import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch, getSession } from '../../api';

const {
  FiCpu, FiKey, FiEye, FiEyeOff, FiSave, FiTrash2, FiCheck,
  FiAlertTriangle, FiRefreshCw, FiPlus, FiToggleLeft, FiToggleRight,
  FiGlobe, FiZap, FiLink, FiShield, FiCode, FiActivity,
  FiSettings, FiInfo, FiChevronDown, FiChevronUp, FiX,
} = FiIcons;

// ── LLM Provider Definitions ──────────────────────────────────────────────────
const LLM_PROVIDERS = [
  {
    id: 'openai',
    name: 'OpenAI',
    models: ['gpt-4o', 'gpt-4-turbo', 'gpt-4', 'gpt-3.5-turbo'],
    keyPrefix: 'sk-',
    keyPlaceholder: 'sk-proj-...',
    color: 'from-emerald-500 to-teal-600',
    iconBg: 'bg-emerald-50 text-emerald-600',
    badge: 'Recommended',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    desc: 'Industry-leading models for legal document generation with high accuracy.',
    docsUrl: 'https://platform.openai.com/api-keys',
  },
  {
    id: 'anthropic',
    name: 'Claude (Anthropic)',
    models: ['claude-opus-4-5', 'claude-sonnet-4-5', 'claude-3-5-sonnet-20241022', 'claude-3-haiku-20240307'],
    keyPrefix: 'sk-ant-',
    keyPlaceholder: 'sk-ant-api03-...',
    color: 'from-orange-500 to-amber-600',
    iconBg: 'bg-orange-50 text-orange-600',
    badge: 'Best for Legal',
    badgeColor: 'bg-orange-100 text-orange-700',
    desc: 'Exceptional reasoning and nuanced language — ideal for legal drafting.',
    docsUrl: 'https://console.anthropic.com/settings/keys',
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    models: ['deepseek-chat', 'deepseek-reasoner'],
    keyPrefix: 'sk-',
    keyPlaceholder: 'sk-...',
    color: 'from-blue-500 to-indigo-600',
    iconBg: 'bg-blue-50 text-blue-600',
    badge: 'Cost Efficient',
    badgeColor: 'bg-blue-100 text-blue-700',
    desc: 'High-performance reasoning at a fraction of the cost. Great for volume.',
    docsUrl: 'https://platform.deepseek.com/api_keys',
  },
  {
    id: 'grok',
    name: 'Grok (xAI)',
    models: ['grok-3', 'grok-3-mini', 'grok-2-1212'],
    keyPrefix: 'xai-',
    keyPlaceholder: 'xai-...',
    color: 'from-gray-700 to-gray-900',
    iconBg: 'bg-gray-100 text-gray-700',
    badge: 'Real-time',
    badgeColor: 'bg-gray-100 text-gray-600',
    desc: 'Real-time knowledge and fast responses powered by xAI.',
    docsUrl: 'https://console.x.ai/',
  },
];

// ── Helper ─────────────────────────────────────────────────────────────────────
const maskKey = (key) => {
  if (!key || key.length < 12) return '••••••••••••••••';
  return key.slice(0, 8) + '••••••••••••' + key.slice(-4);
};

// ── Toggle ─────────────────────────────────────────────────────────────────────
const Toggle = ({ active, onToggle }) => (
  <button type="button" onClick={onToggle}
    className={`w-11 h-6 rounded-full relative transition-colors flex-shrink-0 ${active ? 'bg-accent-gold' : 'bg-brand-dark'}`}>
    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${active ? 'right-1' : 'left-1'}`} />
  </button>
);

// ── Provider Card ──────────────────────────────────────────────────────────────
const ProviderCard = ({ provider, record, onSave, onDelete, onToggle, activeProvider, onSetActive }) => {
  const [apiKey, setApiKey]     = useState('');
  const [model, setModel]       = useState(provider.models[0]);
  const [showKey, setShowKey]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [expanded, setExpanded] = useState(false);

  const hasKey = !!record;
  const isActive = activeProvider === provider.id;

  useEffect(() => {
    if (record) setModel(record.model || provider.models[0]);
  }, [record, provider.models]);

  const handleSave = async () => {
    if (!apiKey.trim()) return;
    setSaving(true);
    await onSave(provider.id, apiKey.trim(), model);
    setApiKey('');
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    setSaving(false);
  };

  return (
    <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-[28px] border-2 overflow-hidden transition-all ${
        isActive ? 'border-accent-gold shadow-xl shadow-accent-gold/10' : 'border-brand-dark hover:border-accent-navy/30'
      }`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${provider.color} p-5 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center">
            <SafeIcon icon={FiCpu} className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-serif text-lg text-white">{provider.name}</h4>
              <span className={`text-[8px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest ${provider.badgeColor}`}>
                {provider.badge}
              </span>
            </div>
            <p className="text-white/60 text-[10px] font-light">{provider.desc}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasKey && (
            <div className="flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-xl">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-white text-[9px] font-black uppercase tracking-widest">Configured</span>
            </div>
          )}
          <button onClick={() => setExpanded(!expanded)}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-all text-white">
            <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
            className="overflow-hidden">
            <div className="p-6 space-y-5">
              {/* Current Key Status */}
              {hasKey && (
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-100 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiKey} className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-black text-green-800">API Key Stored</p>
                      <p className="text-[10px] text-green-600 font-mono">{maskKey(record.api_key_encrypted)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => onSetActive(provider.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                        isActive
                          ? 'bg-accent-gold text-white'
                          : 'bg-white border border-brand-dark text-text-muted hover:border-accent-gold'
                      }`}>
                      <SafeIcon icon={FiZap} className="w-3 h-3" />
                      {isActive ? 'Active' : 'Set Active'}
                    </button>
                    <button onClick={() => onDelete(record.id)}
                      className="p-1.5 rounded-xl border border-red-200 text-red-400 hover:bg-red-50 transition-all">
                      <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Model Selection */}
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">
                  Default Model
                </label>
                <select value={model} onChange={e => setModel(e.target.value)}
                  className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium text-accent-navy">
                  {provider.models.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              {/* API Key Input */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">
                    {hasKey ? 'Update API Key' : 'Enter API Key'}
                  </label>
                  <a href={provider.docsUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[9px] text-accent-gold hover:underline font-black uppercase tracking-widest">
                    <SafeIcon icon={FiLink} className="w-3 h-3" /> Get Key
                  </a>
                </div>
                <div className="relative">
                  <SafeIcon icon={FiKey} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    placeholder={provider.keyPlaceholder}
                    className="w-full pl-11 pr-12 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-mono"
                  />
                  <button onClick={() => setShowKey(!showKey)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-navy transition-colors">
                    <SafeIcon icon={showKey ? FiEyeOff : FiEye} className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[10px] text-text-muted mt-1.5 font-light">
                  Keys are stored encrypted. Expected prefix: <code className="bg-brand-light px-1.5 py-0.5 rounded font-mono text-accent-navy">{provider.keyPrefix}</code>
                </p>
              </div>

              <button onClick={handleSave} disabled={saving || !apiKey.trim()}
                className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50 ${
                  saved ? 'bg-green-500 text-white' : 'bg-accent-navy text-white hover:bg-accent-navy/90'
                }`}>
                {saved
                  ? <><SafeIcon icon={FiCheck} className="w-4 h-4" /> Saved!</>
                  : saving
                  ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <><SafeIcon icon={FiSave} className="w-4 h-4" /> {hasKey ? 'Update Key' : 'Save Key'}</>
                }
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── Webhook Config ─────────────────────────────────────────────────────────────
const WebhookConfig = ({ config, onChange }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="bg-white rounded-[28px] border border-brand-dark p-6 space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center">
          <SafeIcon icon={FiGlobe} className="w-5 h-5" />
        </div>
        <div>
          <h4 className="font-serif text-lg text-accent-navy">Webhook Endpoints</h4>
          <p className="text-[10px] text-text-muted font-light">Receive AI service events in real-time</p>
        </div>
      </div>

      {[
        { key: 'will_generated', label: 'Will Generated', placeholder: 'https://your-server.com/webhooks/will-generated' },
        { key: 'payment_confirmed', label: 'Payment Confirmed', placeholder: 'https://your-server.com/webhooks/payment' },
        { key: 'subscription_updated', label: 'Subscription Updated', placeholder: 'https://your-server.com/webhooks/subscription' },
        { key: 'error_alert', label: 'Error / Alert', placeholder: 'https://your-server.com/webhooks/error' },
      ].map(wh => (
        <div key={wh.key}>
          <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">{wh.label}</label>
          <div className="relative">
            <SafeIcon icon={FiLink} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
            <input
              type="url"
              value={config[wh.key] || ''}
              onChange={e => onChange({ ...config, [wh.key]: e.target.value })}
              placeholder={wh.placeholder}
              className="w-full pl-11 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-mono"
            />
          </div>
        </div>
      ))}

      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">Webhook Secret</label>
        <div className="relative">
          <SafeIcon icon={FiShield} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
          <input
            type={show ? 'text' : 'password'}
            value={config.secret || ''}
            onChange={e => onChange({ ...config, secret: e.target.value })}
            placeholder="whsec_..."
            className="w-full pl-11 pr-12 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-mono"
          />
          <button onClick={() => setShow(!show)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-navy">
            <SafeIcon icon={show ? FiEyeOff : FiEye} className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── MCP Protocol Config ────────────────────────────────────────────────────────
const MCPConfig = ({ config, onChange }) => (
  <div className="bg-white rounded-[28px] border border-brand-dark p-6 space-y-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 bg-accent-navy/5 text-accent-navy rounded-2xl flex items-center justify-center">
        <SafeIcon icon={FiCode} className="w-5 h-5" />
      </div>
      <div>
        <h4 className="font-serif text-lg text-accent-navy">MCP Protocol</h4>
        <p className="text-[10px] text-text-muted font-light">Model Context Protocol for tool-use integration</p>
      </div>
    </div>

    <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl flex items-start gap-3">
      <SafeIcon icon={FiInfo} className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-blue-700 font-light leading-relaxed">
        MCP enables the AI drafting service to call external tools (jurisdiction APIs, legal databases, notarisation services) during will generation.
      </p>
    </div>

    {[
      { key: 'server_url', label: 'MCP Server URL', placeholder: 'https://mcp.your-server.com', type: 'url' },
      { key: 'server_token', label: 'Server Token', placeholder: 'mcp_token_...', type: 'password' },
      { key: 'jurisdiction_tool', label: 'Jurisdiction Tool Endpoint', placeholder: '/tools/jurisdiction-lookup', type: 'text' },
      { key: 'legal_db_tool', label: 'Legal Database Tool', placeholder: '/tools/legal-db', type: 'text' },
    ].map(field => (
      <div key={field.key}>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">{field.label}</label>
        <input
          type={field.type}
          value={config[field.key] || ''}
          onChange={e => onChange({ ...config, [field.key]: e.target.value })}
          placeholder={field.placeholder}
          className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-mono"
        />
      </div>
    ))}

    <div className="grid grid-cols-2 gap-4">
      {[
        { key: 'enable_tools', label: 'Enable Tool Use' },
        { key: 'enable_streaming', label: 'Enable Streaming' },
      ].map(toggle => (
        <div key={toggle.key} className="flex items-center justify-between p-3 bg-brand-light rounded-2xl border border-brand-dark">
          <span className="text-xs font-bold text-accent-navy">{toggle.label}</span>
          <Toggle active={!!config[toggle.key]} onToggle={() => onChange({ ...config, [toggle.key]: !config[toggle.key] })} />
        </div>
      ))}
    </div>
  </div>
);

// ── Global AI Settings ─────────────────────────────────────────────────────────
const GlobalAISettings = ({ settings, onChange }) => (
  <div className="bg-white rounded-[28px] border border-brand-dark p-6 space-y-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 bg-accent-gold/10 text-accent-gold rounded-2xl flex items-center justify-center">
        <SafeIcon icon={FiSettings} className="w-5 h-5" />
      </div>
      <div>
        <h4 className="font-serif text-lg text-accent-navy">Global AI Settings</h4>
        <p className="text-[10px] text-text-muted font-light">Behaviour applied across all providers</p>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">Max Tokens</label>
        <input type="number" value={settings.max_tokens || 4096} min={512} max={128000}
          onChange={e => onChange({ ...settings, max_tokens: parseInt(e.target.value) })}
          className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
      </div>
      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">Temperature</label>
        <input type="number" value={settings.temperature || 0.3} min={0} max={2} step={0.1}
          onChange={e => onChange({ ...settings, temperature: parseFloat(e.target.value) })}
          className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
      </div>
      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">Request Timeout (sec)</label>
        <input type="number" value={settings.timeout || 60} min={10} max={300}
          onChange={e => onChange({ ...settings, timeout: parseInt(e.target.value) })}
          className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
      </div>
      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">Retry Attempts</label>
        <input type="number" value={settings.retries || 3} min={0} max={10}
          onChange={e => onChange({ ...settings, retries: parseInt(e.target.value) })}
          className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
      </div>
    </div>

    <div className="space-y-3">
      {[
        { key: 'content_filter', label: 'Enable Content Filtering', desc: 'Block inappropriate or harmful will content' },
        { key: 'audit_logging', label: 'Audit Logging', desc: 'Log all AI requests for compliance review' },
        { key: 'fallback_enabled', label: 'Provider Fallback', desc: 'Auto-switch to backup provider on failure' },
        { key: 'pii_masking', label: 'PII Masking in Logs', desc: 'Mask personal identifiers in log outputs' },
      ].map(s => (
        <div key={s.key} className="flex items-center justify-between p-4 bg-brand-light rounded-2xl border border-brand-dark hover:border-accent-gold/30 transition-colors">
          <div>
            <p className="text-xs font-bold text-accent-navy">{s.label}</p>
            <p className="text-[10px] text-text-muted font-light">{s.desc}</p>
          </div>
          <Toggle active={!!settings[s.key]} onToggle={() => onChange({ ...settings, [s.key]: !settings[s.key] })} />
        </div>
      ))}
    </div>
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const AIProviderSettings = () => {
  const user = getSession();
  const [records, setRecords]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [activeProvider, setActiveProvider] = useState(null);
  const [activeTab, setActiveTab]           = useState('providers');
  const [saving, setSaving]                 = useState(false);
  const [savedGlobal, setSavedGlobal]       = useState(false);
  const [webhooks, setWebhooks]             = useState({});
  const [mcpConfig, setMcpConfig]           = useState({ enable_tools: false, enable_streaming: true });
  const [globalSettings, setGlobalSettings] = useState({
    max_tokens: 4096, temperature: 0.3, timeout: 60, retries: 3,
    content_filter: true, audit_logging: true, fallback_enabled: true, pii_masking: true,
  });

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await writeFetch('read/llm_api_keys_1748000000?limit=50');
      const data = await res.json();
      const rows = Array.isArray(data.data) ? data.data : [];
      setRecords(rows);
      const activeRec = rows.find(r => r.is_active === 1 || r.is_active === '1');
      if (activeRec) setActiveProvider(activeRec.provider);
    } catch { setRecords([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleSaveKey = async (providerId, apiKey, model) => {
    const existing = records.find(r => r.provider === providerId);
    if (existing) {
      await writeFetch(`update/llm_api_keys_1748000000/${existing.id}`, {
        method: 'PUT',
        body: JSON.stringify({ api_key_encrypted: apiKey, model }),
      });
    } else {
      await writeFetch('create/llm_api_keys_1748000000', {
        method: 'POST',
        body: JSON.stringify({
          id: crypto.randomUUID(),
          owner_id: user?.id,
          provider: providerId,
          api_key_encrypted: apiKey,
          model,
          is_active: 0,
        }),
      });
    }
    fetchKeys();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this API key?')) return;
    await writeFetch(`delete/llm_api_keys_1748000000/${id}`, { method: 'DELETE' });
    fetchKeys();
  };

  const handleSetActive = async (providerId) => {
    // Deactivate all, then activate selected
    for (const rec of records) {
      await writeFetch(`update/llm_api_keys_1748000000/${rec.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: rec.provider === providerId ? 1 : 0 }),
      });
    }
    setActiveProvider(providerId);
    fetchKeys();
  };

  const handleSaveGlobal = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 600)); // simulate save
    setSavedGlobal(true);
    setTimeout(() => setSavedGlobal(false), 2000);
    setSaving(false);
  };

  const configuredCount = records.length;
  const tabs = [
    { id: 'providers', label: 'LLM Providers', icon: FiCpu },
    { id: 'webhooks', label: 'Webhooks', icon: FiGlobe },
    { id: 'mcp', label: 'MCP Protocol', icon: FiCode },
    { id: 'settings', label: 'Global Settings', icon: FiSettings },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-accent-navy to-purple-900 text-white rounded-[32px] p-8 flex items-center gap-6 relative overflow-hidden shadow-2xl">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #fff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="w-16 h-16 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0 z-10">
          <SafeIcon icon={FiCpu} className="w-8 h-8" />
        </div>
        <div className="z-10 flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h2 className="font-serif text-2xl">AI Provider Settings</h2>
            <span className="text-[9px] px-3 py-1 bg-accent-gold text-accent-navy rounded-full font-black uppercase tracking-widest">Super Admin Only</span>
          </div>
          <p className="text-white/60 text-sm font-light">
            Configure LLM providers, API keys, webhook endpoints, and MCP protocols for the AI Will Drafting service.
          </p>
        </div>
        <div className="z-10 flex-shrink-0 hidden md:grid grid-cols-2 gap-3">
          {[
            { label: 'Providers Configured', value: configuredCount },
            { label: 'Active Provider', value: activeProvider ? LLM_PROVIDERS.find(p => p.id === activeProvider)?.name?.split(' ')[0] || '—' : '—' },
          ].map(stat => (
            <div key={stat.label} className="bg-white/10 rounded-2xl p-4 text-center border border-white/10">
              <p className="text-2xl font-serif font-bold text-accent-gold">{stat.value}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-white/50 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Active Provider Banner */}
      {activeProvider && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 p-4 bg-accent-gold/10 border border-accent-gold/20 rounded-2xl">
          <div className="w-9 h-9 bg-accent-gold text-white rounded-xl flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiZap} className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-black text-accent-navy">
              Active AI Provider: <span className="text-accent-gold">{LLM_PROVIDERS.find(p => p.id === activeProvider)?.name}</span>
            </p>
            <p className="text-[10px] text-text-muted font-light">All will drafting requests will route through this provider.</p>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-green-600 font-black uppercase tracking-widest">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" /> Live
          </div>
        </motion.div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-brand-light border border-brand-dark rounded-2xl p-1.5 overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all ${
              activeTab === tab.id ? 'bg-accent-navy text-white shadow-lg' : 'text-text-muted hover:text-accent-navy'
            }`}>
            <SafeIcon icon={tab.icon} className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>

          {/* Providers Tab */}
          {activeTab === 'providers' && (
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-12 h-12 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
                </div>
              ) : (
                LLM_PROVIDERS.map(provider => (
                  <ProviderCard
                    key={provider.id}
                    provider={provider}
                    record={records.find(r => r.provider === provider.id)}
                    onSave={handleSaveKey}
                    onDelete={handleDelete}
                    onToggle={() => {}}
                    activeProvider={activeProvider}
                    onSetActive={handleSetActive}
                  />
                ))
              )}
              <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl flex items-start gap-3">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 font-light leading-relaxed">
                  API keys are stored in the database. For production, consider using a secrets manager. Only one provider can be <strong>Active</strong> at a time — this is the provider that processes all AI will drafting requests.
                </p>
              </div>
            </div>
          )}

          {/* Webhooks Tab */}
          {activeTab === 'webhooks' && (
            <div className="space-y-4">
              <WebhookConfig config={webhooks} onChange={setWebhooks} />
              <div className="p-5 bg-white rounded-[28px] border border-brand-dark">
                <h5 className="text-sm font-black text-accent-navy mb-3">Webhook Event Reference</h5>
                <div className="space-y-2">
                  {[
                    { event: 'will.generated', desc: 'Fired when a will draft PDF is generated', method: 'POST' },
                    { event: 'payment.confirmed', desc: 'Fired on successful plan purchase', method: 'POST' },
                    { event: 'subscription.updated', desc: 'Plan upgrade, downgrade, or cancellation', method: 'POST' },
                    { event: 'ai.error', desc: 'Provider error or timeout', method: 'POST' },
                  ].map(ev => (
                    <div key={ev.event} className="flex items-center gap-3 p-3 bg-brand-light rounded-xl">
                      <span className="text-[8px] px-2 py-1 bg-accent-navy text-white rounded-lg font-black font-mono">{ev.method}</span>
                      <span className="text-[10px] font-mono font-bold text-accent-navy">{ev.event}</span>
                      <span className="text-[10px] text-text-muted font-light flex-1">{ev.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={handleSaveGlobal} disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-4 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all disabled:opacity-60">
                {savedGlobal ? <><SafeIcon icon={FiCheck} /> Saved!</> : saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><SafeIcon icon={FiSave} /> Save Webhook Config</>}
              </button>
            </div>
          )}

          {/* MCP Tab */}
          {activeTab === 'mcp' && (
            <div className="space-y-4">
              <MCPConfig config={mcpConfig} onChange={setMcpConfig} />
              <button onClick={handleSaveGlobal} disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-4 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all disabled:opacity-60">
                {savedGlobal ? <><SafeIcon icon={FiCheck} /> Saved!</> : saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><SafeIcon icon={FiSave} /> Save MCP Config</>}
              </button>
            </div>
          )}

          {/* Global Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-4">
              <GlobalAISettings settings={globalSettings} onChange={setGlobalSettings} />
              <button onClick={handleSaveGlobal} disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-4 bg-accent-gold text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-all shadow-lg shadow-accent-gold/20 disabled:opacity-60">
                {savedGlobal ? <><SafeIcon icon={FiCheck} /> Settings Saved!</> : saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><SafeIcon icon={FiSave} /> Save Global Settings</>}
              </button>
            </div>
          )}

        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AIProviderSettings;